import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, User, Building2, Phone, Mail, Download, Plus, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const STATUS_LABELS = {
  nouveau: { label: "Nouveau", cls: "bg-blue-100 text-blue-800" },
  en_cours: { label: "En cours", cls: "bg-amber-100 text-amber-800" },
  traite: { label: "Traité", cls: "bg-green-100 text-green-800" },
  archive: { label: "Archivé", cls: "bg-slate-100 text-slate-700" },
};

// Placeholder documents / factures — future
const DOCUMENTS = [
  { name: "Contrat cadre 2026.pdf", date: "01/01/2026", size: "142 Ko" },
  { name: "Facture 2026-001.pdf", date: "15/01/2026", size: "88 Ko" },
  { name: "Bon d'intervention Janv.pdf", date: "31/01/2026", size: "56 Ko" },
];

export default function ClientArea() {
  const { user } = useAuth();
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/devis/mine");
        setDevis(data);
      } catch (_) {
        setDevis([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <section className="pt-16 pb-8 bg-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#2196F3] mb-3">Espace client</div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900">Bonjour, {user?.name?.split(" ")[0]} 👋</h1>
              <p className="text-slate-600 mt-2">Suivez vos devis, consultez vos documents et vos informations.</p>
            </div>
            <Link to="/devis">
              <Button className="rounded-full bg-[#2196F3] hover:bg-[#1976D2] h-12 px-6" data-testid="client-new-devis">
                <Plus className="mr-2 h-4 w-4" /> Nouveau devis
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <Tabs defaultValue="devis" className="space-y-8">
            <TabsList className="bg-white border border-slate-100 p-1 h-auto">
              <TabsTrigger value="devis" data-testid="client-tab-devis" className="data-[state=active]:bg-[#2196F3] data-[state=active]:text-white px-5 py-2 rounded-md">
                Mes devis
              </TabsTrigger>
              <TabsTrigger value="documents" data-testid="client-tab-documents" className="data-[state=active]:bg-[#2196F3] data-[state=active]:text-white px-5 py-2 rounded-md">
                Documents
              </TabsTrigger>
              <TabsTrigger value="profile" data-testid="client-tab-profile" className="data-[state=active]:bg-[#2196F3] data-[state=active]:text-white px-5 py-2 rounded-md">
                Profil
              </TabsTrigger>
            </TabsList>

            <TabsContent value="devis">
              <div className="bg-white rounded-3xl border border-slate-100 p-6 md:p-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display font-bold text-xl text-slate-900 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#2196F3]" /> Historique des demandes
                  </h2>
                </div>
                {loading ? (
                  <div className="text-slate-500 py-10 text-center">Chargement…</div>
                ) : devis.length === 0 ? (
                  <div className="text-center py-14 text-slate-500" data-testid="client-devis-empty">
                    <Clock className="h-10 w-10 mx-auto mb-4 opacity-30" />
                    <div className="font-medium mb-1">Aucune demande pour l'instant</div>
                    <div className="text-sm">Faites votre première demande de devis dès maintenant.</div>
                    <Link to="/devis"><Button className="mt-6 rounded-full bg-[#2196F3]">Nouveau devis</Button></Link>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Prestation</TableHead>
                          <TableHead>Superficie</TableHead>
                          <TableHead>Fréquence</TableHead>
                          <TableHead>Statut</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {devis.map((d) => {
                          const s = STATUS_LABELS[d.status] || STATUS_LABELS.nouveau;
                          return (
                            <TableRow key={d.id}>
                              <TableCell className="text-sm">{new Date(d.created_at).toLocaleDateString("fr-FR")}</TableCell>
                              <TableCell className="font-medium">{d.service_type}</TableCell>
                              <TableCell>{d.surface_area} m²</TableCell>
                              <TableCell className="text-sm">{d.frequency}</TableCell>
                              <TableCell><Badge className={s.cls}>{s.label}</Badge></TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="documents">
              <div className="bg-white rounded-3xl border border-slate-100 p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-display font-bold text-xl text-slate-900">Documents et factures</h2>
                  <span className="text-xs text-slate-500 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">Aperçu</span>
                </div>
                <div className="space-y-3">
                  {DOCUMENTS.map((d, i) => (
                    <div key={i} className="flex items-center justify-between p-4 rounded-xl border border-slate-100 hover:border-[#2196F3]/40" style={{ transitionProperty: "border-color", transitionDuration: "200ms" }}>
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-[#E3F2FD] flex items-center justify-center">
                          <FileText className="h-5 w-5 text-[#2196F3]" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-900">{d.name}</div>
                          <div className="text-xs text-slate-500">{d.date} · {d.size}</div>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-[#2196F3]" data-testid={`client-doc-download-${i}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="mt-6 text-xs text-slate-500">
                  Vos documents seront ajoutés par votre chargé de compte au fil des interventions.
                </div>
              </div>
            </TabsContent>

            <TabsContent value="profile">
              <div className="bg-white rounded-3xl border border-slate-100 p-6 md:p-8 space-y-6">
                <h2 className="font-display font-bold text-xl text-slate-900">Vos informations</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <InfoRow icon={User} label="Nom" value={user?.name} />
                  <InfoRow icon={Mail} label="Email" value={user?.email} />
                  <InfoRow icon={Phone} label="Téléphone" value={user?.phone || "—"} />
                  <InfoRow icon={Building2} label="Société" value={user?.company || "—"} />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
      <div className="h-10 w-10 rounded-lg bg-white flex items-center justify-center">
        <Icon className="h-5 w-5 text-[#2196F3]" />
      </div>
      <div>
        <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
        <div className="font-medium text-slate-900 mt-0.5">{value}</div>
      </div>
    </div>
  );
}
